
import { Firestore, DocumentReference, doc, getDoc } from "firebase/firestore";
import { Offer, OfferLocationData } from "@/types/offer";
import { toast } from "sonner";

// Extract location ID from offer data
export const extractLocationId = (offer: Offer): string | null => {
  try {
    if (typeof offer.locationRef === 'string') {
      // If locationRef is a string path like "campLocations/xyz"
      const parts = offer.locationRef.split('/');
      return parts[parts.length - 1];
    } else if (offer.locationRef && typeof offer.locationRef.id === 'string') {
      // If locationRef is a DocumentReference
      return offer.locationRef.id;
    }
    return null;
  } catch (error) {
    console.error("Error extracting locationId:", error);
    return null;
  }
};

// Fetch organization data from offer
export const fetchOrganizationData = async (
  db: Firestore,
  offer: Offer
): Promise<DocumentReference | null> => {
  try {
    // Get organization reference from offer
    const orgRef = offer.locationData?.providedByOrg;
    
    if (!orgRef) {
      console.log("No organization reference found in offer");
      return null;
    }
    
    // Handle different types of orgRef
    if (typeof orgRef === 'object' && orgRef.path) {
      // If it's already a DocumentReference
      return orgRef as DocumentReference;
    } else if (typeof orgRef === 'string') {
      // If it's a path string like "organizations/xyz"
      const parts = (orgRef as string).split('/');
      if (parts.length === 2) {
        return doc(db, parts[0], parts[1]);
      }
    }
    
    console.log("Invalid organization reference format");
    return null;
  } catch (error) {
    console.error("Error fetching organization data:", error);
    return null;
  }
};

// Fetch photo data from location
export const fetchPhotoData = async (
  locationData: OfferLocationData | undefined
): Promise<{ photoPath?: string; photoYOffset?: number } | null> => {
  if (!locationData) return null;
  
  return {
    photoPath: locationData.photoPath,
    photoYOffset: locationData.photoYOffset
  };
};

// Create a safe copy of offer data (sanitizes undefined values)
export const createSafeOfferCopy = (sourceOffer: Offer): Partial<Offer> => {
  try {
    // Create a deep copy to avoid modifying the original
    const copyData = JSON.parse(JSON.stringify(sourceOffer));
    
    // Remove fields that should not be copied
    delete copyData.id;
    delete copyData.createdAt;
    delete copyData.lastEditedAt;
    delete copyData.createdBy;
    delete copyData.lastEditedBy;
    
    return copyData;
  } catch (error) {
    console.error("Error creating safe offer copy:", error);
    toast.error("Failed to prepare session data for copying");
    return {};
  }
};

// Log offer copy data for debugging
export const logOfferCopyData = (
  offerId: string,
  newOfferId: string,
  dateAdjustmentDays: number
): void => {
  console.log(
    `Successfully copied offer ${offerId} to new offer ${newOfferId}` +
    (dateAdjustmentDays !== 0 ? ` with dates adjusted by ${dateAdjustmentDays} days` : "")
  );
};

// Handle copy error
export const handleCopyError = (error: unknown, offerId: string): void => {
  console.error(`Error copying offer ${offerId}:`, error);
  toast.error("Failed to copy session");
};
